import {
  faMobileAlt,
  faServer,
  faDatabase,
  faPalette,
  faNetworkWired,
  faCloud,
  faShoppingCart,
  faCheckCircle,
  faCogs,
  faGraduationCap,
  faMapMarkerAlt,
  faHandshake,
  faTrophy,
  faPuzzlePiece,
  faTools,
} from '@fortawesome/free-solid-svg-icons';

// Google Calendar appointment schedule: Calendar > Create > Appointment schedule > Share > copy booking page link.
export const BOOKING_URL = 'https://calendar.app.google/BkeKmQTFZRzRT1i18';

export const trustData = [
  {
    title: 'Top Berkeley Engineers',
    description: "Expert engineers trained at one of the world's leading institutions, ensuring exceptional solutions.",
    details: 'Our team of engineers is composed of Berkeley graduates with extensive experience in software development. Their expertise ensures every project is executed with precision and innovation.',
    icon: faGraduationCap,
  },
  {
    title: '100% Locally Built',
    description: 'All development is done locally in Southern California, ensuring complete accountability and quality.',
    details: 'We pride ourselves on developing software locally, avoiding outsourcing to maintain the highest standards of quality and accountability.',
    icon: faMapMarkerAlt,
  },
  {
    title: 'Transparent Process',
    description: 'Regular updates with daily screenshots and recordings ensure you’re always in the loop.',
    details: 'Stay informed at every stage of your project. We provide daily updates, detailed progress reports, and regular check-ins to keep you in the loop.',
    icon: faHandshake,
  },
  {
    title: 'Proven Results',
    description: 'Delivering high-quality solutions that meet timelines, budgets, and client expectations.',
    details: 'Our track record speaks for itself—high-quality software delivered on time and within budget, ensuring satisfaction every step of the way.',
    icon: faTrophy,
  },
  {
    title: 'Custom Solutions',
    description: 'Every solution is custom-built to fit your business requirements, ensuring a perfect match.',
    details: 'Your business is unique, and so are our solutions. We tailor every project to align perfectly with your goals and requirements.',
    icon: faPuzzlePiece,
  },
  {
    title: 'State-of-the-Art Tools',
    description: 'Leveraging the latest tools and technologies to ensure your project is future-ready.',
    details: 'We stay ahead of the curve by using the latest development tools and technologies, ensuring your software is modern, scalable, and secure.',
    icon: faTools,
  },
];

export const services = [
  {
    title: 'App Development',
    description: 'From idea to the App Store, we bring your app vision to life.',
    details: 'We build cross-platform mobile applications with scalable architecture, seamless user experiences, and App Store submission support.',
    icon: faMobileAlt,
  },
  {
    title: 'Full-Stack Web Development',
    description: 'Beautiful, functional websites that scale with your business.',
    details: 'Our web development services include responsive design, custom backend APIs, and scalable front-end solutions tailored to your business needs.',
    icon: faServer,
  },
  {
    title: 'Database Architecture',
    description: 'Robust database solutions to handle all your business needs.',
    details: 'We design secure, scalable database solutions with advanced query optimization, schema design, and data modeling services.',
    icon: faDatabase,
  },
  {
    title: 'UI/UX Design',
    description: 'Intuitive and modern interfaces designed for seamless user experiences.',
    details: 'We create intuitive and visually appealing interfaces that improve usability and drive user engagement.',
    icon: faPalette,
  },
  {
    title: 'API Development',
    description: 'Custom APIs to power integrations and scale your applications.',
    details: 'Our API development services include RESTful APIs, GraphQL solutions, and third-party integrations to streamline your applications.',
    icon: faNetworkWired,
  },
  {
    title: 'Cloud Integration',
    description: 'Secure and scalable cloud solutions tailored to your business needs.',
    details: 'We integrate your applications with cloud platforms like AWS, Azure, or Google Cloud for scalability, reliability, and cost-effectiveness.',
    icon: faCloud,
  },
  {
    title: 'E-Commerce Solutions',
    description: 'Custom online stores with seamless user experiences and payment integrations.',
    details: 'We build robust e-commerce platforms with secure payment gateways, inventory management, and a focus on driving sales.',
    icon: faShoppingCart,
  },
  {
    title: 'Quality Assurance & Testing',
    description: 'Ensure your software is bug-free and reliable with thorough testing services.',
    details: 'Our QA services include functional testing, regression testing, and automation to deliver software that meets the highest standards.',
    icon: faCheckCircle,
  },
  {
    title: 'Custom Software Development',
    description: 'Tailored software solutions to meet your unique business challenges.',
    details: 'We build custom software tailored to your specific business needs, ensuring scalability and long-term reliability.',
    icon: faCogs,
  },
];

export const projects = [
  {
    title: 'JackedRabbit',
    description: 'A flexible fitness membership app with access to top gyms, boutique studios, and wellness experiences, plus in-app class booking.',
    technologies: ['React Native', 'Firebase', 'Stripe'],
    link: 'https://apps.apple.com/us/app/jackedrabbit/id6747449627',
    type: 'App',
    logo: '/logos/JackedRabbit.png',
  },
  {
    title: 'FitBridge',
    description: 'A fitness tracking app for comparing workout stats with friends, using live health data.',
    technologies: ['Swift UI', 'Supabase', 'iOS'],
    link: 'https://apps.apple.com/us/app/fitbridge/id6740018770?platform=iphone',
    type: 'App',
    logo: '/logos/FitBridge.png',
  },
  {
    title: 'Daily Lingua',
    description: 'A language learning app delivering daily phrases to help users improve vocabulary and pronunciation.',
    technologies: ['Swift UI', 'Firebase', 'iOS'],
    link: 'https://apps.apple.com/us/app/daily-lingua/id6738664763?platform=iphone',
    type: 'App',
    logo: '/logos/Daily Lingua.png',
  },
  {
    title: 'Calterra Realty',
    description: 'A sleek, responsive website for Calterra Realty, showcasing loan services and property management solutions.',
    technologies: ['React', 'Node.js', 'AWS'],
    link: 'https://www.calterrarealty.com/',
    type: 'Website',
    logo: '/logos/CalterraRealty.png',
  },
];

// The testimonials section stays hidden while this list is empty.
// Each entry: { quote: '...', name: 'Jane Doe', role: 'Founder, Company' }
export const testimonials = [];

export const faqs = [
  {
    question: 'How much does a project cost?',
    answer:
      'Every project is quoted individually, because scope varies a lot from one idea to the next. After your free consultation you receive a detailed proposal covering pricing, timeline, technologies, and milestones. Payments are made in installments as each milestone is completed.',
  },
  {
    question: 'How long will my project take?',
    answer:
      "It depends on the scope. Your proposal includes a timeline broken into milestones, so you'll know what gets delivered and when before any work begins.",
  },
  {
    question: 'What happens during the free consultation?',
    answer:
      "We'll send you a short questionnaire beforehand, then use the call to understand your goals and gather everything needed to put together a comprehensive proposal. There's no obligation to move forward.",
  },
  {
    question: 'How will I know how my project is going?',
    answer:
      "You'll get access to our project portal with daily screenshots and recordings, milestone progress, and timelines. Weekly check-in calls are available on request.",
  },
  {
    question: 'Can you publish my app to the App Store?',
    answer:
      'Yes. We handle App Store submission as part of app development, and the apps in our portfolio are live on the App Store today.',
  },
  {
    question: 'Where is your team based?',
    answer:
      'Southern California. All development is done locally by our team, with no outsourcing.',
  },
];
